import { Link, useParams } from '@tanstack/react-router';
import {
  Bot,
  FileText,
  FolderKanban,
  LayoutDashboard,
  LayoutList,
  ListChecks,
  PlayCircle,
  Settings,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { BRAND_NAME } from '../../brand';
import { usePermissions } from '../../features/auth/permissions';
import { useProject } from '../../features/projects/api';
import { useEnumLabels, useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { Logo } from '../ui/logo';

/**
 * Below the `md` breakpoint the sidebar collapses to an icon rail rather than
 * disappearing, so navigation stays reachable on a narrow window.
 */
const NavItem = ({
  to,
  params,
  icon,
  label,
  exact = false,
}: {
  to: string;
  params?: Record<string, string>;
  icon: ReactNode;
  label: string;
  exact?: boolean;
}) => (
  <Link
    to={to}
    {...(params ? { params } : {})}
    activeOptions={{ exact }}
    title={label}
    // The label is hidden on the icon rail, so the accessible name comes from
    // aria-label — one name, at every width.
    aria-label={label}
    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] font-medium text-ink-muted transition-colors hover:bg-sunken hover:text-ink"
    activeProps={{ className: 'bg-accent-soft text-accent-fg hover:bg-accent-soft hover:text-accent-fg' }}
  >
    <span className="text-current" aria-hidden="true">
      {icon}
    </span>
    <span className="hidden md:inline">{label}</span>
  </Link>
);

const GroupLabel = ({ children }: { children: ReactNode }) => (
  <p className="mt-4 hidden px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle md:block">
    {children}
  </p>
);

/**
 * Project sections live in the sidebar, next to the global navigation, so the
 * whole application has one predictable navigation column.
 */
const ProjectSection = ({ projectCode }: { projectCode: string }) => {
  const { t } = useT();
  const { projectStatus } = useEnumLabels();
  const { data: project } = useProject(projectCode);
  const params = { projectCode };

  return (
    <>
      <GroupLabel>
        {project?.name ?? projectCode}
        {/* Archived is a state the reader has to notice while working inside
            the project, and the sidebar is where the project is named. */}
        {project?.status === 'archived' ? (
          <span className="ml-1 rounded bg-sunken px-1 py-0.5 text-[10px] font-medium normal-case text-ink-subtle">
            {projectStatus('archived')}
          </span>
        ) : null}
      </GroupLabel>
      <div className="my-1 h-px bg-border md:hidden" />
      <NavItem
        to="/projects/$projectCode/overview"
        params={params}
        icon={<LayoutList className="size-4" />}
        label={t('nav.overview')}
      />
      <NavItem
        to="/projects/$projectCode/test-cases"
        params={params}
        icon={<FileText className="size-4" />}
        label={t('nav.testCases')}
      />
      <NavItem
        to="/projects/$projectCode/checklists"
        params={params}
        icon={<ListChecks className="size-4" />}
        label={t('nav.checklists')}
      />
      <NavItem
        to="/projects/$projectCode/test-runs"
        params={params}
        icon={<PlayCircle className="size-4" />}
        label={t('nav.testRuns')}
      />
      <NavItem
        to="/projects/$projectCode/settings"
        params={params}
        icon={<Settings className="size-4" />}
        label={t('nav.settings')}
      />
    </>
  );
};

export const Sidebar = ({ className }: { className?: string }) => {
  const { t } = useT();
  const { can } = usePermissions();
  const { projectCode } = useParams({ strict: false }) as { projectCode?: string };

  return (
    <nav
      className={cn(
        'flex w-14 shrink-0 flex-col gap-1 overflow-y-auto border-r border-border bg-surface p-3 md:w-56',
        className,
      )}
      aria-label={t('nav.main')}
    >
      <Link
        to="/"
        className="mb-3 flex items-center gap-2 px-2 py-1"
        title={BRAND_NAME}
        aria-label={BRAND_NAME}
      >
        <Logo />
        <span className="hidden text-[14px] font-semibold tracking-tight text-ink md:inline">
          {BRAND_NAME}
        </span>
      </Link>

      <NavItem
        to="/"
        icon={<LayoutDashboard className="size-4" />}
        label={t('nav.dashboard')}
        exact
      />
      <NavItem
        to="/projects"
        icon={<FolderKanban className="size-4" />}
        label={t('nav.projects')}
      />
      {can('automation:read') ? (
        <NavItem to="/automation" icon={<Bot className="size-4" />} label={t('nav.automation')} />
      ) : null}
      {can('user:read') ? (
        <NavItem to="/users" icon={<Users className="size-4" />} label={t('nav.users')} />
      ) : null}

      {projectCode ? <ProjectSection projectCode={projectCode} /> : null}
    </nav>
  );
};
